import { useEffect, useRef } from 'react';
import { getVendorUpdates } from '../lib/vendorApi';
import { MapBounds, DateRange, VendorDelta } from '../types/types';

interface UseVendorPollingProps {
  mapBounds: MapBounds | null;
  apiDateRange: DateRange;
  isEnabled: boolean;
  onDelta: (delta: VendorDelta) => void;
  lastServerTimeRef: React.MutableRefObject<string | null>;
  intervalMs?: number;
}

export function useVendorPolling({
  mapBounds,
  apiDateRange,
  isEnabled,
  onDelta,
  lastServerTimeRef,
  intervalMs = 20000,
}: UseVendorPollingProps): void {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const backoffRef = useRef(intervalMs);
  const isEnabledRef = useRef(isEnabled);
  const mapBoundsRef = useRef(mapBounds);
  const apiDateRangeRef = useRef(apiDateRange);
  const onDeltaRef = useRef(onDelta);
  const intervalMsRef = useRef(intervalMs);

  // Keep refs in sync so the poll loop always sees current values
  isEnabledRef.current = isEnabled;
  mapBoundsRef.current = mapBounds;
  apiDateRangeRef.current = apiDateRange;
  onDeltaRef.current = onDelta;
  intervalMsRef.current = intervalMs;

  useEffect(() => {
    const poll = async () => {
      // Skip if disabled, no server time yet, or tab is hidden
      if (!isEnabledRef.current || !lastServerTimeRef.current || document.hidden) {
        schedule(intervalMsRef.current);
        return;
      }

      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const delta = await getVendorUpdates(
          lastServerTimeRef.current,
          mapBoundsRef.current,
          apiDateRangeRef.current,
          timezone
        );
        lastServerTimeRef.current = delta.server_time;
        backoffRef.current = intervalMsRef.current;
        if (delta.added.length || delta.updated.length || delta.removed.length) {
          onDeltaRef.current(delta);
        }
        schedule(intervalMsRef.current);
      } catch {
        backoffRef.current = Math.min(backoffRef.current * 2, 300_000);
        schedule(backoffRef.current);
      }
    };

    const schedule = (delay: number) => {
      timeoutRef.current = setTimeout(poll, delay);
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Resume immediately on tab focus
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        schedule(0);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    schedule(intervalMs);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Intentionally empty deps: the loop runs forever and reads current state via refs.
  // Restart on mount only; cleanup on unmount handles teardown.
}
